import Link from 'src/components/Link'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import type { tProductPreview } from 'src/shared/frontendTypes/product'
import type { tItem } from 'src/shared/frontendTypes/wunschliste'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getProductUrl from 'src/functions/productUrl'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'
import PriceDisplay from 'src/components/PriceDisplay'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import DiscountInfos from 'src/components/DiscountInfos'
import styles from './ModernWishlist.module.css'
import type { WishlistCartState } from './useWishlistCart'

export default function WishlistCard({ item, product, onRemove, onAdd, busy, adding, cartState }: {
  item: tItem
  product: tProductPreview
  onRemove: () => void
  onAdd?: () => void
  busy?: boolean
  adding?: boolean
  cartState?: WishlistCartState
}) {
  const { name, metaTitle, price, discount, images, path, attributes, options, onlyStock, stock } = product
  // Preserve the saved variant when opening details or purchasing directly.
  const productUrl = getProductUrl(item.id, path, options)
  const imageUrl = getImageUrl(images[0], item.id, 'sm') || getImageUrl(images[0], item.id, 'xs')
  const title = metaTitle || name
  const priceValue = cartState?.updatedItem?.price ?? (discount ? {
    regularPrice: price, discountPrice: discount.price, discountPriceAfterStock: discount.priceAfterStock,
    stockDiscountLimit: discount.stockDiscountLimit, limitStockDiscountToStock: discount.limitStockDiscountToStock,
    quantityDiscount: discount.quantityDiscount, title: discount.metaTitle, type: discount.type,
    countdownVariant: discount.countdownVariant, dateEnd: discount.dateEnd, showPercent: discount.showPercent,
  } : price)
  const liveStock = cartState?.updatedItem?.stock ?? stock
  const liveOnlyStock = cartState?.updatedItem?.onlyStock ?? onlyStock
  const canAdd = Boolean(onAdd && !product.inquireOnly && product.purchaseMode !== 'inquiry')
  const showQuantityDiscount = Boolean(discount && discount.showPercent !== false
    && isQuantityDiscountAvailable(discount.quantityDiscount, { onlyStock, stock }))

  return <article className={styles.card}>
    <Link href={productUrl} className={styles.image} aria-label={`${title} ansehen`}>
      {imageUrl ? <>
        <img src={imageUrl} alt={name} width={360} height={300} loading='lazy' />
        <AiGeneratedBadge aiGenerated={images[0]?.aiGenerated} />
      </> : <span className={styles.imageFallback}><PhotoOutlinedIcon aria-hidden='true' /><span>Kein Bild verfügbar</span></span>}
    </Link>
    <div className={styles.details}>
      {product.brandTitel && <p className={styles.brand}>{product.brandTitel}</p>}
      <h3 className={styles.title}><Link href={productUrl}>{title}</Link></h3>
      {attributes?.length > 0 && <ul className={styles.attributes} aria-label='Gewählte Ausführung'>
        {attributes.map((attribute, index) => <li key={`${index}-${attribute}`}>{attribute}</li>)}
      </ul>}
      <div className={styles.price}>
        {typeof priceValue !== 'number' && <DiscountInfos discount={priceValue} component='div' variant='body2'
          align='left' clock={{ variant: 'body2' }} size='small' showQuantityDiscount={showQuantityDiscount} />}
        <PriceDisplay price={priceValue} component='p' variant='h6' align='left' onlyStock={liveOnlyStock} stock={liveStock} discountBadgeVariant='inline' />
      </div>
      {canAdd && <button type='button' className={styles.primary} disabled={busy} onClick={onAdd}
        aria-label={`${title} in den Warenkorb legen`}>
        <AddShoppingCartIcon fontSize='small' aria-hidden='true' /> {adding ? 'Wird hinzugefügt …' : 'In den Warenkorb'}
      </button>}
      {cartState?.message && <p className={styles.cartMessage} role={cartState.error ? 'alert' : 'status'}>
        {cartState.message} {cartState.added && <Link href='/warenkorb'>Zum Warenkorb</Link>}
      </p>}
      <div className={styles.actions}>
        <Link href={productUrl} className={canAdd ? styles.textLink : styles.primary}>Zum Produkt <ArrowForwardIcon fontSize='small' aria-hidden='true' /></Link>
        <button type='button' className={styles.remove} onClick={onRemove} aria-label={`${title} aus der Wunschliste entfernen`}>
          <DeleteOutlineIcon fontSize='small' aria-hidden='true' /> Entfernen
        </button>
      </div>
    </div>
  </article>
}
